import { useEffect, useState } from 'react';
import { api, type CloudPayload } from '../api';
import { OperationCard } from '../components/OperationCard';
import { useStore } from '../state/store';
import { Badge, Card, CardBody, EmptyState } from '../design/primitives';

/**
 * Cloud and AI platform.
 *
 * The shadow-AI count is the headline, because it is the number that makes a
 * CISO uncomfortable: workloads running against customer data that never went
 * through review.
 */
export function CloudPlatform() {
  const { bootstrap } = useStore();
  const [data, setData] = useState<CloudPayload>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    api.cloud()
      .then(setData)
      .catch(caught => setError(caught.message));
  }, []);

  if (error && !data) return <EmptyState>Could not load the cloud inventory: {error}</EmptyState>;
  if (!data || !bootstrap) return null;

  const cloudTools = bootstrap.estate.tools.filter((tool: any) => tool.serverName === 'cloud-platform-mcp');

  return (
    <div className="mb-page">
      <Card>
        <CardBody>
          <p className="mb-lede">
            Meridian runs AI workloads across two cloud accounts. Some were reviewed and signed off. Some were not — and
            those are the ones worth finding.
          </p>
          <div className="mb-stat-row">
            <div className="mb-stat">
              <div className="mb-stat__value">{data.summary.workloads}</div>
              <div className="mb-stat__label">AI workloads</div>
            </div>
            <div className={`mb-stat${data.summary.unsanctioned > 0 ? ' mb-stat--warn' : ''}`}>
              <div className="mb-stat__value">{data.summary.unsanctioned}</div>
              <div className="mb-stat__label">Never reviewed</div>
            </div>
            <div className={`mb-stat${data.summary.internetFacing > 0 ? ' mb-stat--warn' : ''}`}>
              <div className="mb-stat__value">{data.summary.internetFacing}</div>
              <div className="mb-stat__label">Internet-facing</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {data.accounts.map((account: any) => (
        <section key={account.id}>
          <h2 className="mb-section-title">
            {account.accountLabel}{' '}
            <span className="mb-section-tag">
              {account.provider.toUpperCase()} · {account.region}
            </span>
          </h2>
          <p className="mb-section-sub">Owned by {account.owner}.</p>
          <Card>
            <CardBody flush>
              <table className="mb-table">
                <caption className="mb-sr">AI workloads in {account.accountLabel}</caption>
                <thead>
                  <tr>
                    <th scope="col">Workload</th>
                    <th scope="col">Service</th>
                    <th scope="col">Model</th>
                    <th scope="col">Data</th>
                    <th scope="col">Exposure</th>
                    <th scope="col">Reviewed</th>
                  </tr>
                </thead>
                <tbody>
                  {account.workloads.map((workload: any) => (
                    <tr key={workload.id}>
                      <td>{workload.name}</td>
                      <td>
                        <code className="mb-mono">{workload.service}</code>
                      </td>
                      <td>
                        <code className="mb-mono">{workload.model}</code>
                      </td>
                      <td>{workload.dataClasses.join(', ')}</td>
                      <td>
                        <Badge tone={workload.exposure === 'internet-facing' ? 'block' : 'neutral'}>
                          {workload.exposure}
                        </Badge>
                      </td>
                      <td>
                        <Badge tone={workload.sanctioned ? 'allow' : 'block'}>
                          {workload.sanctioned ? 'signed off' : 'never reviewed'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </section>
      ))}

      <section>
        <h2 className="mb-section-title">Platform operations</h2>
        <div className="mb-op-grid">
          {cloudTools.map((tool: any) => (
            <OperationCard key={tool.name} tool={tool} hostId="ro-dev-lt-07" />
          ))}
        </div>
      </section>
    </div>
  );
}
